import React, { useEffect, useState } from 'react';
import {
    IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton,
    IonContent, IonIcon, IonText, IonSpinner, IonCheckbox, IonBadge,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { OnlineUserResponse } from '@/types/response/enterpriseResponses';

interface SignedInUsersModalProps {
    isOpen: boolean;
    onDidDismiss: () => void;
    users: OnlineUserResponse[];
    isLoading: boolean;
    selectedIds: number[];
    onSelectionChange: (ids: number[]) => void;
}

const SignedInUsersModal: React.FC<SignedInUsersModalProps> = ({
    isOpen, onDidDismiss, users, isLoading, selectedIds, onSelectionChange,
}) => {
    const [localSelected, setLocalSelected] = useState<number[]>([]);

    useEffect(() => {
        if (isOpen) setLocalSelected(selectedIds);
    }, [isOpen, selectedIds]);

    const toggle = (id: number) => {
        setLocalSelected(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleDone = () => {
        onSelectionChange(localSelected);
        onDidDismiss();
    };

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onDidDismiss}
            style={{ '--width': '520px', '--height': '420px', '--border-radius': '8px' }}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle style={{ fontSize: '1rem' }}>Signed In Users</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={onDidDismiss}><IonIcon icon={closeOutline} /></IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent style={{ '--padding-start': '1.25rem', '--padding-end': '1.25rem', '--padding-top': '1rem' }}>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><IonSpinner /></div>
                ) : users.length === 0 ? (
                    <IonText color="medium" style={{ display: 'block', textAlign: 'center', padding: '2rem' }}>
                        No users currently signed in.
                    </IonText>
                ) : (
                    <div>
                        <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <IonText style={{ fontSize: '0.85rem', color: 'var(--ion-color-medium)' }}>
                                Select users to message
                            </IonText>
                            {localSelected.length > 0 && (
                                <IonBadge color="primary">{localSelected.length} selected</IonBadge>
                            )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                            {users.map(u => (
                                <div key={u.Id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '1px solid var(--ion-color-light-shade)' }}>
                                    <IonCheckbox checked={localSelected.includes(u.Id)} onIonChange={() => toggle(u.Id)} />
                                    <div>
                                        <div style={{ fontWeight: '500', fontSize: '0.9rem' }}>{u.FirstName} {u.LastName}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>{u.UserName}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </IonContent>

            <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--ion-color-light-shade)', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <IonButton fill="outline" color="medium" onClick={onDidDismiss}>Cancel</IonButton>
                <IonButton fill="solid" color="primary" onClick={handleDone}>Done</IonButton>
            </div>
        </IonModal>
    );
};

export default SignedInUsersModal;
